// frontend/src/pages/learningpageactions.js
import React from 'react';
import LearningPageContentSection from './learningpagecontentsection';
import LearningPageChat from './learningpagechat';
import {
  getGroupConfig,
  getButtonCaption,
  getSectionKey,
  renderBlock,
} from './richcontenthelpers.js';

const LearningPageActions = ({
  currentMicroChunk,
  currentLanguage,
  passedSubject,
  passedChapter,
  passedTopic,
  passedSubtopic,
  currentChunk,
  activeSection,
  onSectionToggle,
  groupedBlocks,
  blocksReady,
}) => {

  // ── Separate toggle groups from always-visible ───────────────────────────
  const toggleGroups = (groupedBlocks || []).filter(group =>
    !(
      !group.group_label &&
      group.blocks.every(b => 
        b.block_type === 'table' || 
        b.block_type === 'text' ||
        b.block_type === 'flowchart' ||
        b.block_type === 'html'
      )
    )
  );

  // ── Mobile abbreviation ──────────────────────────────────────────────────
  const mobileLabel = (caption) => {
    const abbrevMap = {
      'Explanation' : 'Expln',
      'Examples'    : 'Exmpl',
      'Doubts'      : 'Doubt',
      'Video'       : 'Video',
      'Image'       : 'Image',
      'Diagram'     : 'Diag',
      'Steps'       : 'Steps',
      'Content'     : 'Cntnt',
    };
    return abbrevMap[caption] ||
      (caption.length > 5 ? caption.substring(0, 5) : caption);
  };

  // ── Reusable icon button renderer ────────────────────────────────────────
  const renderIconButton = (key, icon, caption, isActive, activeColor, activeGradient, onClick) => (
    <button
      key={key}
      onClick={onClick}
      title={caption}
      style={{
        background   : isActive ? activeColor : '#f1f5f9',
        color        : isActive ? 'white' : '#475569',
        border       : `1px solid ${isActive ? activeColor : '#e2e8f0'}`,
        padding      : '6px 8px',
        borderRadius : '8px',
        cursor       : 'pointer',
        fontWeight   : '600',
        transition   : 'all 0.2s ease',
        display      : 'flex',
        flexDirection: 'column',
        alignItems   : 'center',
        gap          : '2px',
        minWidth     : '52px',
        flexShrink   : 0,
      }}
    >
      <span style={{ fontSize: '18px', lineHeight: '1' }}>{icon}</span>
      <span style={{ fontSize: '10px', lineHeight: '1.2' }}>
        {mobileLabel(caption)}
      </span>
    </button>
  );

  const contentRef = React.useRef(null);

  React.useEffect(() => {
    if (activeSection && contentRef.current) {
      setTimeout(() => {
        contentRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    }
  }, [activeSection]);

  if (!blocksReady) {
    return (
      <div style={{ marginTop: '16px' }}>
        <div className="rc-action-buttons" style={{ opacity: 0.3 }}>
          {renderIconButton('explanation', '💡', 'Explanation', false, '#4c51bf', '', () => {})}
          {renderIconButton('examples',    '📝', 'Examples',    false, '#38a169', '', () => {})}
          {renderIconButton('chat',        '💬', 'Doubts',      false, '#d53f8c', '', () => {})}
        </div>
      </div>
    );
  }

  
  
  return (
    <div>
      {/* ── All buttons in one row ───────────────────────────────────────── */}
      <div className="rc-action-buttons">

        {/* Rich content toggle buttons */}
        {toggleGroups.map((group) => {
          const sectionKey = getSectionKey(group);
          const isOpen     = activeSection === sectionKey;
          const config     = getGroupConfig(group);
          const caption    = getButtonCaption(group);

          return renderIconButton(
            sectionKey,
            config.icon,
            caption,
            isOpen,
            config.color,
            config.gradient,
            () => onSectionToggle(sectionKey)
          );
        })}

        {/* Explanation button */}
        {renderIconButton(
          'explanation',
          '💡',
          'Explanation',
          activeSection === 'explanation',
          '#4c51bf',
          'linear-gradient(45deg, #4c51bf, #667eea)',
          () => onSectionToggle('explanation')
        )}

        {/* Examples button */}
        {renderIconButton(
          'examples',
          '📝',
          'Examples',
          activeSection === 'examples',
          '#38a169',
          'linear-gradient(45deg, #38a169, #48bb78)',
          () => onSectionToggle('examples')
        )}

        {/* Ask Doubts button */}
        {renderIconButton(
          'chat',
          '💬',
          'Doubts',
          activeSection === 'chat',
          '#d53f8c',
          'linear-gradient(45deg, #d53f8c, #ed64a6)',
          () => onSectionToggle('chat')
        )}

      </div>

      
      
      
      
      {/* ── Unified content area ────────────────────────────────────────── */}
      <div className="rc-content-area" ref={contentRef}>

        {/* Rich content open section */}
        {toggleGroups.map((group) => {
          const sectionKey = getSectionKey(group);
          if (activeSection !== sectionKey) return null;

          return (
            <div
              key={`content_${sectionKey}`}
              style={{
                border       : '1px solid #e2e8f0',
                borderRadius : '8px',
                overflow     : 'hidden',
                background   : 'white',
                marginBottom : '12px',
              }}
            >
              {group.blocks.map(block => renderBlock(block))}
            </div>
          );
        })}

        {/* Explanation content */}
        {activeSection === 'explanation' && (
          <LearningPageContentSection
            currentMicroChunk={currentMicroChunk}
            currentLanguage={currentLanguage}
            type="explanation"
          />
        )}

        {/* Examples content */}
        {activeSection === 'examples' && (
          <LearningPageContentSection
            currentMicroChunk={currentMicroChunk}
            currentLanguage={currentLanguage}
            type="examples"
          />
        )}

        {/* Chat content */}
        {activeSection === 'chat' && (
          <LearningPageChat
            currentMicroChunk={currentMicroChunk}
            currentLanguage={currentLanguage}
            passedSubject={passedSubject}
            passedChapter={passedChapter}
            passedTopic={passedTopic}
            passedSubtopic={passedSubtopic}
            currentChunk={currentChunk}
          />
        )}

      </div>

      
    </div>
  );
};

export default LearningPageActions;